import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState
} from "react";
import { type Control, useForm, useWatch } from "react-hook-form";

import { format } from "date-fns";
import { merge } from "lodash";

import { useAppDispatch, useAppSelector } from "app/hooks";
import {
  setActionType,
  setCustomMessage,
  setLabel,
  setOpen,
  setOpen as setOpenSnackbar
} from "components/action-snackbar/action-snackbar-slice";
import ErrorSnackbarContent from "components/error-snackbar-content";
import type { FolderObject } from "components/folder-picker/folder-picker.component";
import { selectCurrentClinic } from "features/auth/auth-slice";
import { useUploadDocumentMutation } from "services/e-documents";
import { useDownloadTemplateFormMutation } from "services/form-templates";
import {
  useCreateFormMutation,
  useLazyGetFormFileUrlQuery,
  useUpdateFormDocumentMutation
} from "services/forms";
import { useGetPatientQuery } from "services/patients";
import type { Appointment } from "services/types/appointments.type";
import {
  FormTemplate,
  FormTemplatePDFInput
} from "services/types/form-templates.type";
import { convertBlobUrlToFile, openFile } from "utils/common";

export type PatientFormsValues = {
  form_template: FormTemplate | null;
};

export const EMPTY_DEFAULT_VALUES: PatientFormsValues = {
  form_template: null
};

type FormTemplatesContextType = {
  control: Control<PatientFormsValues, any, PatientFormsValues>;
  isValid: boolean;
  isError: boolean;
  isLoading: boolean;
  onSubmit: (e?: React.BaseSyntheticEvent) => Promise<void>;
  pdfFormBlob: Blob | undefined;
  appointment: Appointment;
  patientId: number | undefined;
  showSignAndPrintDialog: boolean;
  handleHideSignAndPrintDialog: () => void;
  handleShowSignAndPrintDialog: () => void;
  pdfFormBlobUrl: string | undefined;
  showSignModal: boolean;
  setShowSignModal: React.Dispatch<React.SetStateAction<boolean>>;
  handleSubmitSignature: (
    files: File[],
    id?: number
  ) => Promise<Blob | undefined>;
  signature: File | undefined;
  onCreateForm: (
    data: PatientFormsValues
  ) => Promise<{ id?: number; file?: File }>;
  handleSelectBoxFolder: (selectedFolder: FolderObject | null) => Promise<void>;
  openBoxDialog: {
    box_folder_id?: string;
    box_access_token?: string | null;
    open: boolean;
  };
  setOpenBoxDialog: React.Dispatch<
    React.SetStateAction<{
      box_folder_id?: string;
      box_access_token?: string | null;
      open: boolean;
    }>
  >;
  clearValues: () => void;
};

const FormTemplatesContext = createContext<FormTemplatesContextType | null>(
  null
);

const resolveAppointmentId = (
  appt: Appointment | { modelId?: number }
): number | undefined =>
  (appt as { modelId?: number })?.modelId ?? (appt as Appointment)?.id;

type FormTemplatesProviderProps = {
  appointment: Appointment;
  patientId?: number;
  children?: React.ReactNode;
};

export default function FormTemplatesProvider({
  appointment,
  patientId,
  children
}: FormTemplatesProviderProps) {
  const dispatch = useAppDispatch();
  const [isError, setIsError] = useState(false);
  const currentClinic = useAppSelector(selectCurrentClinic);
  const [pdfFormBlob, setPdfFormBlob] = useState<Blob | undefined>();
  const [pdfFormBlobUrl, setPdfFormBlobUrl] = useState<string | undefined>();
  const [showSignAndPrintDialog, setShowSignAndPrintDialog] = useState(false);

  const [isBusy, setIsBusy] = useState<boolean>();
  const [formId, setFormId] = useState<number | undefined>();
  const [signature, setSignature] = useState<File | undefined>();
  const [showSignModal, setShowSignModal] = useState(false);
  const [openBoxDialog, setOpenBoxDialog] = useState<{
    box_folder_id?: string;
    box_access_token?: string | null;
    open: boolean;
  }>({ open: false });

  const { data: patient, isFetching: isPatientFetching } = useGetPatientQuery(
    {
      patientId: Number(patientId),
      query: "{id,first_name,last_name,box_folder_id}"
    },
    {
      skip: !patientId
    }
  );

  const [uploadDocument, { isLoading: isUploadingToEDocs }] =
    useUploadDocumentMutation();

  const [downloadForm, { isLoading }] = useDownloadTemplateFormMutation();
  const [createForm, { isLoading: isCreateFormLoading }] =
    useCreateFormMutation();

  const [updateForm, { isLoading: isUpdateFormLoading }] =
    useUpdateFormDocumentMutation();

  const defaultValues: PatientFormsValues = useMemo(() => {
    return merge({}, EMPTY_DEFAULT_VALUES);
  }, []);

  const {
    handleSubmit,
    control,
    reset,
    formState: { isValid }
  } = useForm<PatientFormsValues>({
    mode: "onBlur",
    defaultValues
  });

  const handleRevokeBlobUrl = useCallback(() => {
    if (pdfFormBlobUrl) {
      URL.revokeObjectURL(pdfFormBlobUrl);
    }
  }, [pdfFormBlobUrl]);

  const clearValues = useCallback(() => {
    reset(defaultValues);
    setIsError(false);
    if (pdfFormBlobUrl) {
      URL.revokeObjectURL(pdfFormBlobUrl);
    }
    setPdfFormBlob(undefined);
    setSignature(undefined);
    setFormId(undefined);
    setPdfFormBlobUrl(undefined);
  }, [defaultValues, pdfFormBlobUrl, reset]);

  useEffect(() => {
    clearValues();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultValues, reset, patientId, appointment]);

  useEffect(() => {
    return () => {
      handleRevokeBlobUrl();
    };
  }, [handleRevokeBlobUrl, pdfFormBlobUrl]);

  const values = useWatch({ control }) as PatientFormsValues;

  const [getFormFileUrl, { isLoading: isGetFormFileUrlLoading }] =
    useLazyGetFormFileUrlQuery();

  const onCreateForm = useCallback(
    async (data: PatientFormsValues) => {
      setPdfFormBlob(undefined);
      setSignature(undefined);
      setFormId(undefined);
      setIsBusy(true);
      setIsError(false);
      const { form_template: formTemplate } = data;
      const inputData = {
        formData: {
          form_template: formTemplate!.id,
          patient: Number(patientId),
          clinic: currentClinic?.id,
          appointment: resolveAppointmentId(appointment)
        },
        asFile: false
      };
      const id = await createForm(inputData).then((response) => {
        if ("error" in response) {
          setIsError(true);
          handleRevokeBlobUrl();
          setPdfFormBlobUrl(undefined);
          setPdfFormBlob(undefined);
          const error = response.error as any;
          if (error.originalStatus === 400) {
            console.error(`CreatePatientForm Error Details: ${error.data}`);
          }
          setIsBusy(false);
        } else {
          setPdfFormBlobUrl(undefined);
          setPdfFormBlob(undefined);

          return response.data.id;
        }
      });

      if (id) {
        const response = await getFormFileUrl({
          formId: Number(id),
          appointment: resolveAppointmentId(appointment),
          clinic: currentClinic?.id
        });
        setIsBusy(false);
        if ("error" in response) {
          setIsError(true);
          handleRevokeBlobUrl();
          setPdfFormBlobUrl(undefined);
          setPdfFormBlob(undefined);
          Promise.resolve({});
        }
        if (response.data) {
          setFormId(id);
          const url = response.data;
          setPdfFormBlobUrl(url);
          const fileName = `${patientId} ${data.form_template?.title}`;

          const result = await convertBlobUrlToFile(url, fileName).then(
            (file) => {
              setPdfFormBlob(file);
              setShowSignModal(true);
              return file;
            }
          );
          return { file: result, id };
        }
      }
      return Promise.resolve({});
    },
    [
      appointment,
      createForm,
      currentClinic?.id,
      getFormFileUrl,
      handleRevokeBlobUrl,
      patientId
    ]
  );

  const onSubmit = useCallback(
    (data: PatientFormsValues) => {
      if (pdfFormBlobUrl) {
        openFile(pdfFormBlobUrl, true);
        return;
      }
      setIsBusy(true);
      setIsError(false);
      const { form_template: formTemplate } = data;
      const inputData = {
        form_template: formTemplate!.id,
        patient: patientId,
        clinic: currentClinic?.id,
        appointment: resolveAppointmentId(appointment)
      } as FormTemplatePDFInput;
      downloadForm(inputData).then((response) => {
        setIsBusy(false);
        if ("error" in response) {
          setIsError(true);
          handleRevokeBlobUrl();
          setPdfFormBlobUrl(undefined);
          setPdfFormBlob(undefined);
          const error = response.error as any;
          if (error.originalStatus === 400) {
            console.error(`GeneratePatientForms Error Details: ${error.data}`);
          }
        } else {
          dispatch(setLabel("Patient Form"));
          dispatch(setOpenSnackbar(true));
          dispatch(setActionType("add"));
          dispatch(setCustomMessage(`'${formTemplate?.title}' Generated!`));

          const blob = response.data;
          const fileUrl = window.URL.createObjectURL(blob);

          setPdfFormBlobUrl(fileUrl);
          setPdfFormBlob(blob);
          openFile(fileUrl, true);
        }
      });
    },
    [
      pdfFormBlobUrl,
      patientId,
      currentClinic?.id,
      appointment,
      downloadForm,
      handleRevokeBlobUrl,
      dispatch
    ]
  );

  const handleShowSignAndPrintDialog = useCallback(() => {
    setShowSignModal(false);
    setShowSignAndPrintDialog(true);

    if (!pdfFormBlobUrl || !formId) {
      onCreateForm(values);
    }
  }, [formId, onCreateForm, pdfFormBlobUrl, values]);

  const handleHideSignAndPrintDialog = useCallback(() => {
    setShowSignAndPrintDialog(false);
    setShowSignModal(false);
    setOpenBoxDialog((prev) => ({ ...prev, open: false }));
  }, []);

  const handleSubmitSignature = useCallback(
    async (files: File[], id?: number): Promise<Blob | undefined> => {
      setIsBusy(true);
      const signature = files?.at(0);
      setSignature(signature);

      const formData = new FormData();
      if (signature) {
        formData.append("signature", signature);
      } else {
        formData.append("signature", "");
      }

      return await updateForm({
        id: Number(id || formId),
        appointment: resolveAppointmentId(appointment),
        clinic: currentClinic?.id,
        asFile: true,
        formData
      }).then((response) => {
        setIsBusy(false);
        if ("error" in response) {
          setIsError(true);
          handleRevokeBlobUrl();
          setPdfFormBlobUrl(undefined);
          setPdfFormBlob(undefined);
          const error = response.error as any;
          if (error.originalStatus === 400) {
            console.error(`GeneratePatientForms Error Details: ${error.data}`);
          }
        } else {
          handleRevokeBlobUrl();

          const blob = response.data as unknown as Blob;
          const fileUrl = window.URL.createObjectURL(blob);

          setPdfFormBlobUrl(fileUrl);
          setPdfFormBlob(blob);
          return blob;
        }
      });
    },
    [appointment, currentClinic?.id, formId, handleRevokeBlobUrl, updateForm]
  );

  const handleSelectBoxFolder = useCallback(
    async (selectedFolder: FolderObject | null) => {
      const formData = new FormData();

      let blob = pdfFormBlob;

      if (patient && blob) {
        formData.append(
          "document",
          blob,
          `form-${format(new Date(), "yyyy-MM-dd")}-${patient?.first_name}-${
            patient?.last_name
          }-${patient?.id}-form-id-${formId}.pdf`
        );
        formData.append(
          "destination_folder_id",
          String(selectedFolder?.id || patient.box_folder_id)
        );

        uploadDocument({
          id: patient.id,
          type: "patients" as const,
          formData: formData
        }).then((response) => {
          if (response && "error" in response) {
            dispatch(
              setCustomMessage(<ErrorSnackbarContent response={response} />)
            );
            dispatch(setOpen(true));
          } else {
            dispatch(
              setCustomMessage("PDF File Was Uploaded to Selected Folder")
            );
            dispatch(setOpen(true));
            handleHideSignAndPrintDialog();
          }
        });
      }
    },
    [
      dispatch,
      formId,
      handleHideSignAndPrintDialog,
      patient,
      pdfFormBlob,
      uploadDocument
    ]
  );

  const isWorkInProgress =
    isLoading ||
    isCreateFormLoading ||
    isUpdateFormLoading ||
    isBusy ||
    isGetFormFileUrlLoading ||
    isPatientFetching ||
    isUploadingToEDocs;

  const value = useMemo(() => {
    return {
      control,
      isValid,
      isError,
      isLoading: isWorkInProgress,
      onSubmit: handleSubmit(onSubmit),
      onCreateForm: onCreateForm,
      pdfFormBlob,
      appointment,
      patientId,
      showSignAndPrintDialog,
      handleShowSignAndPrintDialog,
      handleHideSignAndPrintDialog,
      pdfFormBlobUrl,
      showSignModal,
      setShowSignModal,
      handleSubmitSignature,
      signature,
      handleSelectBoxFolder,
      openBoxDialog,
      setOpenBoxDialog,
      clearValues
    };
  }, [
    control,
    isValid,
    isError,
    isWorkInProgress,
    handleSubmit,
    onSubmit,
    onCreateForm,
    pdfFormBlob,
    appointment,
    patientId,
    showSignAndPrintDialog,
    handleShowSignAndPrintDialog,
    handleHideSignAndPrintDialog,
    pdfFormBlobUrl,
    showSignModal,
    handleSubmitSignature,
    signature,
    handleSelectBoxFolder,
    openBoxDialog,
    clearValues
  ]);
  return (
    <FormTemplatesContext.Provider value={value}>
      {children}
    </FormTemplatesContext.Provider>
  );
}

export const useFormTemplates = () => {
  const context = useContext(FormTemplatesContext);
  if (!context) {
    throw new Error(
      `useFormTemplates must be used within an FormTemplatesProvider`
    );
  }

  return context;
};
